import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject;
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import IconData from './data.json';
import { withVDSManager, calculateRem } from '@vds-core/utilities';
import { ColorTokens } from '@vds-tokens/color';
var propTypes = {
  /**
   * If provided, will render bold icons
   */
  bold: PropTypes.bool,
  /**
   *
   * Name of the icon
   */
  name: PropTypes.oneOf(['checkmark', 'checkmark-alt', 'close', 'down-caret', 'down-caret-bold', 'error', 'info', 'left-caret', 'pagination-left-arrow', 'pagination-right-arrow', 'right-caret', 'warning']),
  /**
   * @ignore
   */
  ariaLabel: PropTypes.string,
  /**
   * Size of the icon.
   */
  size: PropTypes.oneOfType([PropTypes.oneOf(['small', 'medium', 'large', 'XLarge']), PropTypes.string, PropTypes.number]),
  /**
   * Color of the icon.
   */
  color: PropTypes.oneOf(['#000000', '#FFFFFF', '#ffffff', '#EE0000', '#ee0000', '#F6F6F6', '#f6f6f6', '#D8DADA', '#d8dada', '#A7A7A7', '#a7a7a7', '#6F7171', '#6f7171', '#333333', '#1B1D1F', '#1b1d1f', '#FBE4D7', '#fbe4d7', '#ED7000', '#ed7000', '#C44904', '#c44904', '#4A1C02', '#4a1c02', '#FFF4E0', '#fff4e0', '#FFBC3D', '#ffbc3d', '#523C14', '#523c14', '#D6EEFB', '#d6eefb', '#0096E4', '#0096e4', '#0077B4', '#0077b4', '#002B42', '#002b42', '#D6F2E0', '#d6f2e0', '#00AC3E', '#00ac3e', '#008330', '#003614']),
  /**
   * The tabIndex of the icon.
   */
  tabIndex: PropTypes.number,
  /**
   * @ignore
   * passes through icon data object
   */
  data: PropTypes.object,
  /**
   * @ignore
   */
  surface: PropTypes.oneOf(['light', 'dark']),
  /**
   * Allows a unique ID to be passed to the component.
   */
  id: PropTypes.string,
  /**
   * Hides content from assistive technology when set to true
   */
  ariaHidden: PropTypes.bool
};
var defaultProps = {
  size: 'medium',
  color: ColorTokens.elements.primary.onlight.value,
  tabIndex: 0,
  surface: 'light',
  bold: true,
  // props created for 1.0
  data: IconData,
  ariaHidden: false
};
var StyledSVG = /*#__PURE__*/styled.svg.withConfig({
  displayName: "StyledSVG",
  componentId: "VDS__sc-qte7wk-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: inline-block;\n  height: ", ";\n  width: ", ";\n  min-height: ", ";\n  min-width: ", ";\n  ", ";\n  &:active,\n  &:focus {\n    outline: none;\n  }\n"])), function (_ref) {
  var iconHeight = _ref.iconHeight;
  return calculateRem(iconHeight);
}, function (_ref2) {
  var iconWidth = _ref2.iconWidth;
  return calculateRem(iconWidth);
}, function (_ref3) {
  var iconHeight = _ref3.iconHeight;
  return calculateRem(iconHeight);
}, function (_ref4) {
  var iconWidth = _ref4.iconWidth;
  return calculateRem(iconWidth);
}, function (_ref5) {
  var tabIndex = _ref5.tabIndex;
  return tabIndex < 0 && "\n    outline: none;\n    pointer-events: none;\n  ";
});
var getIconSize = function getIconSize(size) {
  if (typeof size === 'number') return calculateRem(size);
  switch (size) {
    case 'XLarge':
      return 28;
    case 'large':
      return 24;
    case 'small':
      return 16;
    case 'medium':
    default:
      return 20;
  }
};
function mapChildren(children, iconColor) {
  return children.length > 0 && children.map(function (child, index) {
    var attr = child.attributes;
    if (child.name === 'g') {
      return /*#__PURE__*/React.createElement("g", {
        key: "".concat(child.name).concat(index)
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'path') {
      var fill;
      if (attr.style) {
        fill = attr.style.split('fill:')[1];
      }
      return /*#__PURE__*/React.createElement("path", {
        key: "".concat(child.name).concat(index),
        d: attr.d,
        stroke: "none",
        fill: !!fill ? fill : attr.fill ? attr.fill : iconColor
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'circle') {
      return /*#__PURE__*/React.createElement("circle", {
        id: attr.id,
        key: "".concat(child.name).concat(index),
        cx: attr.cx,
        cy: attr.cy,
        r: attr.r,
        opacity: attr.opacity,
        stroke: "none",
        fill: attr.fill ? attr.fill : iconColor
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'rect') {
      return /*#__PURE__*/React.createElement("rect", {
        key: "".concat(child.name).concat(index),
        x: attr.x,
        y: attr.y,
        ry: attr.ry,
        rx: attr.rx,
        transform: attr.transform,
        width: attr.width,
        height: attr.height,
        stroke: "none",
        fill: attr.fill ? attr.fill : iconColor
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'polyline') {
      return /*#__PURE__*/React.createElement("polyline", {
        key: "".concat(child.name).concat(index),
        points: attr.points,
        stroke: "none",
        fill: attr.fill ? attr.fill : iconColor
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'line') {
      return /*#__PURE__*/React.createElement("line", {
        key: "".concat(child.name).concat(index),
        x1: attr.x1,
        x2: attr.x2,
        y1: attr.y1,
        y2: attr.y2,
        stroke: "none",
        fill: attr.fill ? attr.fill : iconColor
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'polygon') {
      return /*#__PURE__*/React.createElement("polygon", {
        key: "".concat(child.name).concat(index),
        points: attr.points,
        stroke: "none",
        fill: attr.fill ? attr.fill : iconColor
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'linearGradient') {
      return /*#__PURE__*/React.createElement("linearGradient", {
        key: "".concat(child.name).concat(index),
        id: attr.id,
        gradientUnits: attr.gradientUnits,
        x1: attr.x1,
        y1: attr.y1,
        x2: attr.x2,
        y2: attr.y2,
        gradientTransform: attr.gradientTransform
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
    if (child.name === 'stop') {
      return /*#__PURE__*/React.createElement("stop", {
        offset: attr.offset,
        stopColor: attr['stop-color'],
        key: "".concat(child.name).concat(index)
      }, child.children.length > 0 && mapChildren(child.children, iconColor));
    }
  });
}

/**
 * @ignore
 */
var RESTRICTED_Icon = function RESTRICTED_Icon(props) {
  var ariaHidden = props.ariaHidden,
    name = props.name,
    size = props.size,
    lineColor = props.lineColor,
    color = props.color,
    surface = props.surface,
    data = props.data,
    id = props.id,
    bold = props.bold;
  var iconMap = {
    'pagination-right-arrow': 'right-arrow',
    'pagination-left-arrow': 'left-arrow'
  };
  var calculateIcon = function calculateIcon() {
    var originalIcons = Object.keys(iconMap);
    if (originalIcons.includes(name)) {
      var newName = bold ? name : iconMap[name];
      return data[newName];
    }
    return bold ? data["".concat(name, "-bold")] : data[name];
  };
  var icon = calculateIcon();
  var iconColor = lineColor || color;

  //If inverted and no color specified
  if (surface === 'dark' && !color && !lineColor) {
    iconColor = ColorTokens.elements.primary.ondark.value;
    //If inverted and color black
  } else if (surface === 'dark' && (color === 'black' || color === ColorTokens.elements.primary.onlight.value || color === 'Black')) {
    iconColor = ColorTokens.elements.primary.ondark.value;
  }
  if (!icon) return null;
  return /*#__PURE__*/React.createElement(StyledSVG, {
    id: id,
    data: data,
    "aria-label": props.ariaLabel ? props.ariaLabel : name + ' icon',
    "aria-hidden": ariaHidden,
    tabIndex: props.tabIndex,
    "alt-text": props.ariaLabel ? props.ariaLabel : name + 'icon'
    //Passing rem values through to styled component keeps firefox and safari happy
    ,
    iconWidth: getIconSize(size),
    iconHeight: getIconSize(size),
    viewBox: icon.attributes.viewBox,
    surface: surface
  }, mapChildren(icon.children, iconColor));
};
RESTRICTED_Icon.propTypes = propTypes;
RESTRICTED_Icon.defaultProps = defaultProps;
export default withVDSManager(RESTRICTED_Icon);